/**
 * MetricCard - Tarjeta de métrica reutilizable (UI compartida)
 * Componente presentacional genérico usado por Dashboard y Métricas.
 *
 * Extras opcionales (solo se dibujan si se pasan):
 *  - `trend`: línea de tendencia bajo el valor ("Mejor que ayer" + variación).
 *  - `sparkline`: mini-gráfica de área (a la derecha o apilada, según el ancho).
 *  - `ladder`: escalera Top N (nombre sobre la barra de progreso).
 *  - `ranking`: lista vertical Top 3 con medallas (🥇, 🥈, 🥉).
 *  - `list`: lista compacta con marcador (Stock Crítico).
 *  - `weeklyBars`: barras verticales por día de la semana (Ticket Promedio).
 *  - `gauge`: medidor semicircular (Puntaje de salud).
 *  - `donut`: dona compacta con leyenda (Alertas Activas).
 *  - `treemap`: bloques proporcionales (Cartera por Cobrar).
 *  - `matrix`: matriz de bloques con ejes que llena el cuerpo (Alertas Activas).
 *
 * La tarjeta es `@container`: en anchos < 280 px apila la mini-gráfica bajo el
 * valor para no comprimir el texto, y el valor escala con el ancho (`cqw`).
 */

import React from "react";
import { ArrowRight } from "lucide-react";
import { Card } from "@/components/ui/Card";
import { Badge } from "@/components/ui/Badge";
import { SkeletonMetricCard } from "@/components/ui";
import { useAnimatedNumber } from "@/hooks/useAnimatedNumber";
import { useInViewOnce } from "@/hooks/useInViewOnce";
import { useReducedMotion } from "@/hooks/useReducedMotion";
import { staggerDelay } from "@/lib/animation/motion";
import { MetricDonut } from "./MetricDonut";
import { MetricGauge } from "./MetricGauge";
import { MetricTreemap } from "./MetricTreemap";
import { MetricMatrix } from "./MetricMatrix";
import { MetricProgressBar } from "./MetricProgressBar";
import { MetricProgressScale } from "./MetricProgressScale";
import { MetricProgressStages } from "./MetricProgressStages";
import { MetricGoalBars } from "./MetricGoalBars";
import { MetricRanking } from "./MetricRanking";
import { MetricLadder } from "./MetricLadder";
import { MetricList } from "./MetricList";
import { MetricWeeklyBars } from "./MetricWeeklyBars";
import { MetricSparkline } from "./MetricSparkline";
import { ChartEmptyState } from "./ChartEmptyState";
import type { MetricBadgeTone, MetricCardProps } from "./MetricCard.types";
import {
  badgeToneClasses,
  badgeToneTextClasses,
  badgeToneVariants,
  colorStyles,
  sparklineColorStyles,
  trendStyles,
  weeklyBarFillStyles,
} from "./MetricCard.styles";

export type {
  MetricColor,
  MetricBadgeTone,
  MetricTrend,
  MetricTrendDirection,
} from "./MetricCard.types";
export type { MetricGoalBarPoint } from "./MetricGoalBars";
export type { MetricLadderItem } from "./MetricLadder";
export type { MetricListItem } from "./MetricList";
export type { MetricProgressMilestone } from "./MetricProgressScale";
export type { MetricRankingItem } from "./MetricRanking";
export type {
  MetricSparklinePoint,
  MetricSparklineVariant,
} from "./MetricSparkline";
export type { MetricWeeklyBar } from "./MetricWeeklyBars";

/**
 * MetricCard memoizado para evitar re-renders innecesarios
 * Se usa 4 veces en el dashboard con props similares
 *
 * Regla 13: React.memo para componentes que reciben props frecuentemente
 */
export const MetricCard = React.memo(
  ({
    icon: Icon,
    title,
    value,
    numericValue,
    formatNumericValue,
    numericDecimals = 0,
    animationIndex,
    change,
    statusTone,
    positive,
    color = "blue",
    loading = false,
    subtitle,
    indicator,
    valueClassName,
    valueNoWrap = false,
    onClick,
    sparkline,
    sparklineVariant = "area",
    sparklineShowLabels = false,
    sparklineAxis = false,
    sparklineLayout = "side",
    sparklineLabel,
    trend,
    note,
    progress,
    progressAlign = "start",
    goalBars,
    gauge,
    donut,
    treemap,
    matrix,
    ranking,
    ladder,
    list,
    weeklyBars,
    weeklyBarsAxis = false,
    heightClassName = "h-[212px] sm:h-[200px]",
    className = "",
    empty = false,
  }: MetricCardProps) => {
    const colorStyle = colorStyles[color];
    const reducedMotion = useReducedMotion();
    const hasNumericValue =
      numericValue !== undefined && formatNumericValue !== undefined;

    const { value: animatedValue, direction: valueDirection } =
      useAnimatedNumber(numericValue ?? 0, {
        decimals: numericDecimals,
        enabled: hasNumericValue,
      });

    const { ref: cardRef, inView } = useInViewOnce<HTMLDivElement>({
      enabled: !reducedMotion && !loading,
    });

    if (loading) {
      return <SkeletonMetricCard />;
    }

    const displayValue =
      hasNumericValue && formatNumericValue
        ? formatNumericValue(animatedValue)
        : value;

    /** Destello verde/rojo cuando el valor del KPI sube o baja. */
    const flashClass = reducedMotion
      ? ""
      : valueDirection === "up"
        ? "animate-kpi-flash-up"
        : valueDirection === "down"
          ? "animate-kpi-flash-down"
          : "";

    /** Entrada de la tarjeta (escalonada por `animationIndex`). */
    const entranceClass = reducedMotion || !inView ? "" : "animate-chart-pop";
    const entranceStyle =
      !reducedMotion && animationIndex !== undefined
        ? { animationDelay: staggerDelay(animationIndex) }
        : undefined;

    /**
     * Sin datos: se reutiliza el estado vacío de las gráficas grandes
     * (`ChartEmptyState`) en el cuerpo, conservando el título, el icono y la
     * flecha de acción. El badge de estado y el subtítulo se omiten para no
     * duplicar el mensaje. Se activa con `empty`.
     */
    if (empty) {
      return (
        <Card
          ref={cardRef}
          hover
          padding="none"
          onClick={onClick}
          style={entranceStyle}
          className={`@container relative ${heightClassName} p-4${
            onClick
              ? " cursor-pointer active:scale-[0.98] transition-transform"
              : ""
          }${entranceClass ? ` ${entranceClass}` : ""}${className ? ` ${className}` : ""}`}
        >
          <div className="flex h-full min-h-0 flex-col overflow-hidden">
            <div className="flex shrink-0 items-start gap-1.5">
              {Icon && (
                <Icon
                  className={`h-3.5 w-3.5 flex-shrink-0 ${colorStyle.text}`}
                />
              )}
              <p className="m-0 min-w-0 flex-1 truncate text-[11px] font-medium leading-tight text-slate-500 dark:text-slate-400 sm:whitespace-normal sm:break-words">
                {title}
              </p>
            </div>

            <div className="mt-2 min-h-0 flex-1">
              <ChartEmptyState fillHeight />
            </div>

            {onClick && (
              <div className="mt-auto flex shrink-0 items-end justify-end gap-2 border-t border-gray-100 pt-2 dark:border-slate-700/60">
                <div
                  className={`p-1 rounded-full ${colorStyle.bg} ${colorStyle.text} flex-shrink-0`}
                  aria-hidden="true"
                >
                  <ArrowRight className="w-3 h-3" />
                </div>
              </div>
            )}
          </div>
        </Card>
      );
    }

    /**
     * Tamaño del valor: por defecto escala con el **ancho de la tarjeta**
     * (`cqw`), así una cifra larga nunca se corta en tarjetas estrechas;
     * `valueClassName` lo reemplaza.
     */
    const valueTextClass = valueClassName ?? "text-base";

    /**
     * Tono del badge: `statusTone` explícito o derivado de `positive`
     * (`true` → success, `false` → critical, `undefined` → info).
     */
    const resolvedTone: MetricBadgeTone =
      statusTone ??
      (positive === true
        ? "success"
        : positive === false
          ? "critical"
          : "info");
    const badgeVariant = badgeToneVariants[resolvedTone];

    /**
     * Gap de la zona de datos (gráfica/barras/lista): 14 px si hay subtítulo
     * (`note`), 16 px compensado si no lo hay.
     */
    const vizGapClass = note ? "mt-3.5" : "mt-4";

    /** Separación de la barra de progreso según su alineación en el cuerpo. */
    const progressGapClass =
      progressAlign === "center"
        ? "my-auto"
        : progressAlign === "bottom"
          ? "mt-auto"
          : vizGapClass;

    const hasSparkline = Boolean(sparkline && sparkline.length > 1);
    const stackedSparkline = hasSparkline && sparklineLayout === "stacked";
    const trendStyle = trend ? trendStyles[trend.direction] : null;

    return (
      <Card
        ref={cardRef}
        hover
        padding="none"
        onClick={onClick}
        style={entranceStyle}
        className={`@container relative ${heightClassName} p-4${
          onClick
            ? " cursor-pointer active:scale-[0.98] transition-transform"
            : ""
        }${entranceClass ? ` ${entranceClass}` : ""}${className ? ` ${className}` : ""}`}
      >
        {/* Sticker en esquina superior derecha (siempre fijo) */}
        {change && (
          <div className="absolute -top-px -right-px z-10 hidden sm:flex">
            <Badge
              key={change}
              variant={badgeVariant}
              size="sm"
              className={`rounded-none rounded-bl-lg rounded-tr-xl !text-[9px] !px-1.5 !py-0.5 ${badgeToneClasses[resolvedTone]}${reducedMotion ? "" : " animate-chart-pop"}`}
            >
              {change}
            </Badge>
          </div>
        )}

        {/* Contenido con su propio recorte: el badge de estado queda fuera de
            este contenedor para poder pintar sobre el borde en la esquina. */}
        <div className="flex h-full min-h-0 flex-col justify-between overflow-hidden">
          {/* Ícono + título pegados arriba. Si el título llega al badge, hace
            salto de línea: un badge invisible reserva el ancho exacto del badge
            de estado (que vive en la esquina) para que el texto no lo pise. */}
          <div
            className={`${change ? "sm:-mr-4" : ""} flex shrink-0 items-start gap-1.5`}
          >
            {Icon && (
              <Icon
                className={`h-3.5 w-3.5 flex-shrink-0 ${colorStyle.text}`}
              />
            )}
            <p className="m-0 min-w-0 flex-1 truncate text-[11px] font-medium leading-tight text-slate-500 dark:text-slate-400 sm:whitespace-normal sm:break-words">
              {title}
            </p>
            {change && (
              <span
                aria-hidden="true"
                className="invisible hidden shrink-0 sm:flex"
              >
                <Badge
                  variant={badgeVariant}
                  size="sm"
                  className="!text-[9px] !px-1.5 !py-0.5"
                >
                  {change}
                </Badge>
              </span>
            )}
          </div>

          {/* Gauge/dona: sustituyen al valor/nota/desglose y se centran en el
            espacio libre del cuerpo. */}
          {gauge ? (
            <MetricGauge {...gauge} className="mt-1" />
          ) : donut ? (
            <MetricDonut {...donut} className="mt-1" />
          ) : treemap ? (
            <MetricTreemap {...treemap} className="mt-1" />
          ) : matrix ? (
            <MetricMatrix {...matrix} className="mt-1" />
          ) : (
            /* Valor + tendencia; con mini-gráfica la fila ocupa el alto libre. En
            tarjetas anchas (≥280px) el gráfico va a la derecha; en estrechas se
            apila a todo el ancho bajo el valor para no comprimir el texto. */
            <div
              className={`mt-2 flex min-h-0 flex-1 items-stretch ${
                stackedSparkline ? "gap-2" : note ? "gap-3.5" : "gap-4"
              } ${
                hasSparkline
                  ? stackedSparkline
                    ? "flex-col pb-2"
                    : "flex-col pb-2 @[280px]:flex-row"
                  : ""
              }`}
            >
              <div
                className={`flex min-h-0 min-w-0 flex-col ${
                  stackedSparkline ? "flex-none" : "flex-1"
                }`}
              >
                <div className="flex min-w-0 flex-col items-start gap-0.5">
                  <div
                    key={hasNumericValue ? `kpi-${numericValue}` : undefined}
                    className={`-mx-0.5 min-w-0 max-w-full rounded-md px-0.5 font-semibold leading-tight text-slate-800 dark:text-slate-100 ${
                      valueNoWrap ? "truncate" : "line-clamp-2 break-words"
                    } ${valueTextClass}${flashClass ? ` ${flashClass}` : ""}`}
                  >
                    {displayValue}
                    {indicator && (
                      <span className="ml-1 text-[10px] font-medium text-green-600 dark:text-green-400">
                        {indicator}
                      </span>
                    )}
                  </div>
                  {change && (
                    <span
                      className={`text-[9px] font-semibold leading-tight sm:hidden ${badgeToneTextClasses[resolvedTone]}`}
                    >
                      {change}
                    </span>
                  )}
                </div>

                {note && (
                  <div className="mt-2 min-w-0 text-[9px] leading-snug text-slate-400 dark:text-slate-500">
                    {note}
                  </div>
                )}

                {trend && trendStyle && (
                  <div
                    key={`${trend.direction}-${trend.label}`}
                    className={`mt-0.5 flex min-w-0 items-center gap-1 text-[7px] leading-tight sm:text-[8px] md:text-[9px] ${trendStyle.text}${
                      reducedMotion ? "" : " animate-trend-pop"
                    }`}
                  >
                    <trendStyle.Icon className="w-3 h-3 flex-shrink-0" />
                    <span className="min-w-0 truncate font-semibold">
                      {trend.label}
                    </span>
                    {trend.detail && (
                      <span className="min-w-0 shrink-0 font-medium opacity-90">
                        {trend.detail}
                      </span>
                    )}
                  </div>
                )}

                {goalBars && goalBars.points.length > 0 && (
                  <div className="mt-3 flex min-h-0 flex-1 flex-col justify-center">
                    <MetricGoalBars
                      points={goalBars.points}
                      goal={goalBars.goal}
                      colorTodayByGoal={goalBars.colorTodayByGoal}
                      barClassName={goalBars.barClassName}
                      pendingClassName={goalBars.pendingClassName}
                      reachedClassName={goalBars.reachedClassName}
                    />
                  </div>
                )}

                {progress &&
                  (progress.milestones && progress.milestones.length > 0 ? (
                    <MetricProgressScale
                      value={progress.value}
                      fillClassName={progress.fillClassName}
                      trailingLabel={progress.trailingLabel}
                      milestones={progress.milestones}
                      className={progressGapClass}
                    />
                  ) : progress.stages && progress.stages.length > 0 ? (
                    <MetricProgressStages
                      value={progress.value}
                      fillClassName={progress.fillClassName}
                      trailingLabel={progress.trailingLabel}
                      stages={progress.stages}
                      className={progressGapClass}
                    />
                  ) : progress.trailingLabel ? (
                    <div
                      className={`${progressGapClass} flex items-center gap-2`}
                    >
                      <MetricProgressBar
                        value={progress.value}
                        fillClassName={progress.fillClassName}
                        label={progress.label}
                        heightClassName="h-3"
                        className="min-w-0 flex-1"
                      />
                      <span className="shrink-0 text-[9px] font-semibold tabular-nums text-gray-500 sm:text-[10px] dark:text-slate-400">
                        {progress.trailingLabel}
                      </span>
                    </div>
                  ) : (
                    <MetricProgressBar
                      value={progress.value}
                      fillClassName={progress.fillClassName}
                      label={progress.label}
                      heightClassName="h-3"
                      className={`${progressGapClass} w-full`}
                    />
                  ))}

                {ladder && ladder.length > 0 && (
                  <MetricLadder items={ladder} className={vizGapClass} />
                )}

                {ranking && ranking.length > 0 && (
                  <MetricRanking items={ranking} className={vizGapClass} />
                )}

                {list && list.length > 0 && (
                  <MetricList items={list} className={vizGapClass} />
                )}

                {weeklyBars && weeklyBars.length > 0 && (
                  <MetricWeeklyBars
                    items={weeklyBars}
                    fillClassName={weeklyBarFillStyles[color]}
                    showAxis={weeklyBarsAxis}
                    className={vizGapClass}
                  />
                )}
              </div>

              {hasSparkline && sparkline && (
                <div
                  role="img"
                  aria-label={sparklineLabel ?? "Tendencia de los últimos días"}
                  className={
                    stackedSparkline
                      ? `w-full flex-1 min-h-0 ${sparklineColorStyles[color]}`
                      : `h-12 w-full flex-shrink-0 sm:h-16 ${
                          sparklineAxis
                            ? "self-center @[280px]:h-14"
                            : "self-stretch @[280px]:h-auto"
                        } @[280px]:w-[38%] @[280px]:min-w-[3.5rem] @[280px]:max-w-[9rem] ${sparklineColorStyles[color]}`
                  }
                >
                  <div
                    className={
                      stackedSparkline ? "h-full min-h-0" : "h-full w-full"
                    }
                  >
                    <MetricSparkline
                      points={sparkline}
                      variant={sparklineVariant}
                      showLabels={sparklineShowLabels}
                      showAxis={sparklineAxis}
                    />
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Subtítulo + flecha de acción en fila propia al pie, con línea divisoria integrada */}
          {(subtitle || onClick) && (
            <div className="mt-auto flex shrink-0 items-end justify-between gap-2 border-t border-gray-100 pt-2 dark:border-slate-700/60">
              {subtitle ? (
                <div className="min-w-0 text-[9px] leading-tight text-slate-400 dark:text-slate-500">
                  {subtitle}
                </div>
              ) : (
                <div />
              )}
              {onClick && (
                <div
                  className={`p-1 rounded-full ${colorStyle.bg} ${colorStyle.text} flex-shrink-0`}
                  aria-hidden="true"
                >
                  <ArrowRight className="w-3 h-3" />
                </div>
              )}
            </div>
          )}
        </div>
      </Card>
    );
  },
);
